<template>
  <div class="kb-page">
    <AppHeader />

    <!-- Hero -->
    <section class="kb-calc-hero">
      <div class="kb-calc-hero__overlay"></div>
      <div class="container-xl kb-calc-hero__content" data-aos="fade-up">
        <div class="kb-page-hero__breadcrumb">
          <router-link to="/">Home</router-link>
          <i class="bi bi-chevron-right mx-2"></i>
          <span>Tarpaulin Size Calculator</span>
        </div>
        <h1>Tarpaulin Size Calculator</h1>
        <p>Find the exact tarpaulin size for your truck, warehouse or custom requirement — instantly.</p>
        <div class="kb-calc-hero__badges">
          <span><i class="bi bi-truck me-1"></i>19 Vehicle Types</span>
          <span><i class="bi bi-building me-1"></i>8 Warehouse Sizes</span>
          <span><i class="bi bi-rulers me-1"></i>Custom Dimensions</span>
          <span><i class="bi bi-patch-check-fill me-1"></i>Free Tool</span>
        </div>
      </div>
    </section>

    <!-- Tab Navigation -->
    <div class="kb-calc-tabs-bar">
      <div class="container-xl">
        <div class="kb-calc-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="kb-calc-tab"
            :class="{ active: activeTab === tab.key }"
            @click="$router.push('/calculator/' + tab.key); resetAll(); scrollToTabs()"
          >
            <i :class="tab.icon + ' me-2'"></i>{{ tab.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── SECTION 1: Vehicle Calculator ── -->
    <section v-show="activeTab === 'vehicle'" class="kb-calc-section">
      <div class="container-xl">
        <div class="kb-calc-layout">

          <!-- Left: Input Panel -->
          <div class="kb-calc-panel" data-aos="fade-right">
            <div class="kb-calc-panel__header">
              <i class="bi bi-truck-front-fill"></i>
              <div>
                <h2>Vehicle Tarpaulin Calculator</h2>
                <p>Select your truck or tempo type to get the recommended tarpaulin size.</p>
              </div>
            </div>

            <!-- Step 1: Category -->
            <div class="kb-calc-step">
              <label class="kb-calc-label">Step 1 — Select Vehicle Category</label>
              <div class="kb-calc-category-grid">
                <button
                  v-for="cat in vehicleCategories"
                  :key="cat.key"
                  class="kb-calc-cat-btn"
                  :class="{ active: selectedVehicleCategory === cat.key }"
                  @click="selectVehicleCategory(cat.key)"
                >
                  <i :class="cat.icon"></i>
                  <span>{{ cat.key }}</span>
                  <small>{{ (cat.label.split('\u2013')[1] || cat.label).trim() }}</small>
                </button>
              </div>
            </div>

            <!-- Step 2: Vehicle Selection -->
            <div class="kb-calc-step" v-if="selectedVehicleCategory">
              <label class="kb-calc-label">Step 2 — Select Your Vehicle</label>
              <div class="kb-calc-vehicle-list">
                <button
                  v-for="v in filteredVehicles"
                  :key="v.vehicle"
                  class="kb-calc-vehicle-btn"
                  :class="{ active: selectedVehicle && selectedVehicle.vehicle === v.vehicle }"
                  @click="selectedVehicle = v"
                >
                  <i class="bi bi-truck"></i>
                  <div>
                    <strong>{{ v.vehicle }}</strong>
                    <small>{{ v.dimensions.length }} × {{ v.dimensions.width }} × {{ v.dimensions.height }} ft</small>
                  </div>
                  <i class="bi bi-chevron-right arrow"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Right: Result Panel -->
          <div class="kb-calc-result" data-aos="fade-left" data-aos-delay="100">
            <!-- Placeholder -->
            <div v-if="!selectedVehicle" class="kb-calc-result__empty">
              <i class="bi bi-truck-front"></i>
              <h3>Select a vehicle to see the recommended tarpaulin size</h3>
              <p>We'll calculate the exact size based on vehicle dimensions plus required overlap margin.</p>
              <div class="kb-calc-result__formula-box">
                <p class="formula-title">Formula Used:</p>
                <code>Length = L + (2 × H) + Margin</code>
                <code>Width = W + (2 × H) + Margin</code>
              </div>
            </div>

            <!-- Result -->
            <div v-else class="kb-calc-result__card">
              <div class="kb-calc-result__vehicle-info">
                <span class="kb-calc-result__badge">{{ selectedVehicle.category }}</span>
                <h3>{{ selectedVehicle.vehicle }}</h3>
                <div class="kb-calc-result__dims">
                  <div class="dim-item">
                    <span>Length</span><strong>{{ selectedVehicle.dimensions.length }} ft</strong>
                  </div>
                  <div class="dim-item">
                    <span>Width</span><strong>{{ selectedVehicle.dimensions.width }} ft</strong>
                  </div>
                  <div class="dim-item">
                    <span>Height</span><strong>{{ selectedVehicle.dimensions.height }} ft</strong>
                  </div>
                  <div class="dim-item">
                    <span>Margin</span><strong>{{ selectedVehicle.extra_margin }} ft</strong>
                  </div>
                </div>
              </div>

              <div class="kb-calc-result__output">
                <p class="kb-calc-result__output-label">Recommended Tarpaulin Size</p>
                <div class="kb-calc-result__size">
                  <div class="size-box">
                    <span>{{ roundUpTo3(selectedVehicle.tarpaulin.length) }} ft</span>
                    <small>Length</small>
                  </div>
                  <div class="size-multiply">×</div>
                  <div class="size-box">
                    <span>{{ roundUpTo3(selectedVehicle.tarpaulin.width) }} ft</span>
                    <small>Width</small>
                  </div>
                </div>
                <p class="kb-calc-result__note">
                  <i class="bi bi-info-circle me-1"></i>
                  Size rounded up to the nearest standard sheet size (multiple of 3 ft)
                </p>
              </div>

              <div class="kb-calc-result__breakdown">
                <p class="breakdown-title">How we calculated this:</p>
                <div class="breakdown-row">
                  <span>Length = {{ selectedVehicle.dimensions.length }} + (2 × {{ selectedVehicle.dimensions.height }}) + {{ selectedVehicle.extra_margin }}</span>
                  <strong>= {{ selectedVehicle.tarpaulin.length }} ft → {{ roundUpTo3(selectedVehicle.tarpaulin.length) }} ft</strong>
                </div>
                <div class="breakdown-row">
                  <span>Width = {{ selectedVehicle.dimensions.width }} + (2 × {{ selectedVehicle.dimensions.height }}) + {{ selectedVehicle.extra_margin }}</span>
                  <strong>= {{ selectedVehicle.tarpaulin.width }} ft → {{ roundUpTo3(selectedVehicle.tarpaulin.width) }} ft</strong>
                </div>
              </div>

              <div class="kb-calc-result__cta">
                <a
                  :href="`https://wa.me/919892310123?text=Hello%2C%20I%20need%20a%20tarpaulin%20of%20size%20${roundUpTo3(selectedVehicle.tarpaulin.length)}%20x%20${roundUpTo3(selectedVehicle.tarpaulin.width)}%20ft%20for%20my%20${encodeURIComponent(selectedVehicle.vehicle)}`"
                  target="_blank"
                  class="kb-btn kb-btn--whatsapp"
                >
                  <i class="bi bi-whatsapp me-2"></i>Get Quote for This Size
                </a>
                <button class="kb-btn kb-btn--outline" @click="selectedVehicle = null; selectedVehicleCategory = null">
                  <i class="bi bi-arrow-counterclockwise me-2"></i>Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Vehicle Size Reference Table -->
        <div class="kb-calc-table-section" data-aos="fade-up">
          <h3 class="kb-calc-table-title">
            <i class="bi bi-table me-2"></i>All Vehicle Tarpaulin Size Reference
          </h3>
          <div class="kb-calc-table-wrap">
            <table class="kb-calc-table">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Category</th>
                  <th>Vehicle Size (L × W × H ft)</th>
                  <th>Recommended Tarpaulin Size</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="v in vehicleData"
                  :key="v.vehicle"
                  :class="{ highlighted: selectedVehicle && selectedVehicle.vehicle === v.vehicle }"
                  @click="selectedVehicle = v; selectedVehicleCategory = v.category; scrollToResult()"
                  style="cursor: pointer"
                >
                  <td><strong>{{ v.vehicle }}</strong></td>
                  <td><span class="kb-cat-pill">{{ v.category }}</span></td>
                  <td>{{ v.dimensions.length }} × {{ v.dimensions.width }} × {{ v.dimensions.height }}</td>
                  <td>
                    <strong class="kb-size-result">
                      {{ roundUpTo3(v.tarpaulin.length) }} × {{ roundUpTo3(v.tarpaulin.width) }} ft
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

    <!-- ── SECTION 2: Warehouse Calculator ── -->
    <section v-show="activeTab === 'warehouse'" class="kb-calc-section">
      <div class="container-xl">
        <div class="kb-calc-layout">

          <!-- Left: Input Panel -->
          <div class="kb-calc-panel" data-aos="fade-right">
            <div class="kb-calc-panel__header">
              <i class="bi bi-buildings-fill"></i>
              <div>
                <h2>Warehouse Tarpaulin Calculator</h2>
                <p>Select your godown or warehouse type to get the ideal tarpaulin coverage size.</p>
              </div>
            </div>

            <!-- Step 1: Category -->
            <div class="kb-calc-step">
              <label class="kb-calc-label">Step 1 — Select Warehouse Category</label>
              <div class="kb-calc-category-grid kb-calc-category-grid--2">
                <button
                  v-for="cat in warehouseCategories"
                  :key="cat.key"
                  class="kb-calc-cat-btn"
                  :class="{ active: selectedWarehouseCategory === cat.key }"
                  @click="selectWarehouseCategory(cat.key)"
                >
                  <i :class="cat.icon"></i>
                  <span>{{ cat.key }}</span>
                  <small>{{ cat.label }}</small>
                </button>
              </div>
            </div>

            <!-- Step 2: Warehouse Selection -->
            <div class="kb-calc-step" v-if="selectedWarehouseCategory">
              <label class="kb-calc-label">Step 2 — Select Warehouse Type</label>
              <div class="kb-calc-vehicle-list">
                <button
                  v-for="w in filteredWarehouses"
                  :key="w.warehouse"
                  class="kb-calc-vehicle-btn"
                  :class="{ active: selectedWarehouse && selectedWarehouse.warehouse === w.warehouse }"
                  @click="selectedWarehouse = w"
                >
                  <i class="bi bi-building"></i>
                  <div>
                    <strong>{{ w.warehouse }}</strong>
                    <small>{{ w.dimensions.length }} × {{ w.dimensions.width }} × {{ w.dimensions.height }} ft</small>
                  </div>
                  <i class="bi bi-chevron-right arrow"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Right: Result Panel -->
          <div class="kb-calc-result" data-aos="fade-left" data-aos-delay="100">
            <!-- Placeholder -->
            <div v-if="!selectedWarehouse" class="kb-calc-result__empty">
              <i class="bi bi-buildings"></i>
              <h3>Select a warehouse type to get the tarpaulin coverage size</h3>
              <p>Perfect for covering godowns, storage sheds, and industrial facilities during monsoon or construction.</p>
              <div class="kb-calc-result__formula-box">
                <p class="formula-title">Formula Used:</p>
                <code>Length = L + (2 × H) + Margin</code>
                <code>Width = W + (2 × H) + Margin</code>
              </div>
            </div>

            <!-- Result -->
            <div v-else class="kb-calc-result__card">
              <div class="kb-calc-result__vehicle-info">
                <span class="kb-calc-result__badge kb-calc-result__badge--green">{{ selectedWarehouse.category }}</span>
                <h3>{{ selectedWarehouse.warehouse }}</h3>
                <div class="kb-calc-result__dims">
                  <div class="dim-item">
                    <span>Length</span><strong>{{ selectedWarehouse.dimensions.length }} ft</strong>
                  </div>
                  <div class="dim-item">
                    <span>Width</span><strong>{{ selectedWarehouse.dimensions.width }} ft</strong>
                  </div>
                  <div class="dim-item">
                    <span>Height</span><strong>{{ selectedWarehouse.dimensions.height }} ft</strong>
                  </div>
                  <div class="dim-item">
                    <span>Margin</span><strong>{{ selectedWarehouse.extra_margin }} ft</strong>
                  </div>
                </div>
              </div>

              <div class="kb-calc-result__output">
                <p class="kb-calc-result__output-label">Recommended Tarpaulin Size</p>
                <div class="kb-calc-result__size">
                  <div class="size-box">
                    <span>{{ roundUpTo3(selectedWarehouse.tarpaulin.length) }} ft</span>
                    <small>Length</small>
                  </div>
                  <div class="size-multiply">×</div>
                  <div class="size-box">
                    <span>{{ roundUpTo3(selectedWarehouse.tarpaulin.width) }} ft</span>
                    <small>Width</small>
                  </div>
                </div>
                <p class="kb-calc-result__note">
                  <i class="bi bi-info-circle me-1"></i>
                  Size rounded up to the nearest standard sheet size (multiple of 3 ft)
                </p>
              </div>

              <div class="kb-calc-result__breakdown">
                <p class="breakdown-title">How we calculated this:</p>
                <div class="breakdown-row">
                  <span>Length = {{ selectedWarehouse.dimensions.length }} + (2 × {{ selectedWarehouse.dimensions.height }}) + {{ selectedWarehouse.extra_margin }}</span>
                  <strong>= {{ selectedWarehouse.tarpaulin.length }} ft → {{ roundUpTo3(selectedWarehouse.tarpaulin.length) }} ft</strong>
                </div>
                <div class="breakdown-row">
                  <span>Width = {{ selectedWarehouse.dimensions.width }} + (2 × {{ selectedWarehouse.dimensions.height }}) + {{ selectedWarehouse.extra_margin }}</span>
                  <strong>= {{ selectedWarehouse.tarpaulin.width }} ft → {{ roundUpTo3(selectedWarehouse.tarpaulin.width) }} ft</strong>
                </div>
              </div>

              <div class="kb-calc-result__cta">
                <a
                  :href="`https://wa.me/919892310123?text=Hello%2C%20I%20need%20a%20tarpaulin%20of%20size%20${roundUpTo3(selectedWarehouse.tarpaulin.length)}%20x%20${roundUpTo3(selectedWarehouse.tarpaulin.width)}%20ft%20for%20my%20${encodeURIComponent(selectedWarehouse.warehouse)}`"
                  target="_blank"
                  class="kb-btn kb-btn--whatsapp"
                >
                  <i class="bi bi-whatsapp me-2"></i>Get Quote for This Size
                </a>
                <button class="kb-btn kb-btn--outline" @click="selectedWarehouse = null; selectedWarehouseCategory = null">
                  <i class="bi bi-arrow-counterclockwise me-2"></i>Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Warehouse Size Reference Table -->
        <div class="kb-calc-table-section" data-aos="fade-up">
          <h3 class="kb-calc-table-title">
            <i class="bi bi-table me-2"></i>All Warehouse Tarpaulin Size Reference
          </h3>
          <div class="kb-calc-table-wrap">
            <table class="kb-calc-table">
              <thead>
                <tr>
                  <th>Warehouse Type</th>
                  <th>Category</th>
                  <th>Dimensions (L × W × H ft)</th>
                  <th>Margin</th>
                  <th>Recommended Tarpaulin Size</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="w in warehouseData"
                  :key="w.warehouse"
                  :class="{ highlighted: selectedWarehouse && selectedWarehouse.warehouse === w.warehouse }"
                  @click="selectedWarehouse = w; selectedWarehouseCategory = w.category; scrollToResult()"
                  style="cursor: pointer"
                >
                  <td><strong>{{ w.warehouse }}</strong></td>
                  <td><span class="kb-cat-pill kb-cat-pill--green">{{ w.category }}</span></td>
                  <td>{{ w.dimensions.length }} × {{ w.dimensions.width }} × {{ w.dimensions.height }}</td>
                  <td>{{ w.extra_margin }} ft</td>
                  <td>
                    <strong class="kb-size-result">
                      {{ roundUpTo3(w.tarpaulin.length) }} × {{ roundUpTo3(w.tarpaulin.width) }} ft
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

    <!-- ── SECTION 3: Custom Calculator ── -->
    <section v-show="activeTab === 'custom'" class="kb-calc-section">
      <div class="container-xl">
        <div class="kb-calc-layout">

          <!-- Left: Input Panel -->
          <div class="kb-calc-panel" data-aos="fade-right">
            <div class="kb-calc-panel__header">
              <i class="bi bi-rulers"></i>
              <div>
                <h2>Custom Size Calculator</h2>
                <p>Enter your area dimensions and overlap margin — we'll tell you the exact tarpaulin size to order.</p>
              </div>
            </div>

            <div class="kb-calc-step">
              <label class="kb-calc-label">Enter Your Dimensions (in feet)</label>

              <div class="kb-custom-form">
                <div class="kb-custom-field">
                  <label>Length (ft)</label>
                  <div class="kb-custom-input-wrap">
                    <i class="bi bi-arrows-expand-vertical"></i>
                    <input
                      v-model.number="custom.length"
                      type="number"
                      min="1"
                      placeholder="e.g. 20"
                      @input="computeCustom"
                    />
                    <span>ft</span>
                  </div>
                </div>

                <div class="kb-custom-field">
                  <label>Width (ft)</label>
                  <div class="kb-custom-input-wrap">
                    <i class="bi bi-arrows-expand"></i>
                    <input
                      v-model.number="custom.width"
                      type="number"
                      min="1"
                      placeholder="e.g. 15"
                      @input="computeCustom"
                    />
                    <span>ft</span>
                  </div>
                </div>

                <div class="kb-custom-field">
                  <label>Extra Margin / Overlap (ft)</label>
                  <p class="kb-custom-field__hint">Additional length on each side to secure/tie the tarpaulin</p>
                  <div class="kb-custom-input-wrap">
                    <i class="bi bi-border-outer"></i>
                    <input
                      v-model.number="custom.margin"
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="e.g. 2"
                      @input="computeCustom"
                    />
                    <span>ft</span>
                  </div>
                  <div class="kb-margin-presets">
                    <button
                      v-for="m in [1, 1.5, 2, 3]"
                      :key="m"
                      class="kb-margin-preset"
                      :class="{ active: custom.margin === m }"
                      @click="custom.margin = m; computeCustom()"
                    >{{ m }} ft</button>
                  </div>
                </div>

                <button class="kb-btn kb-btn--navy w-100" @click="computeCustom" :disabled="!custom.length || !custom.width">
                  <i class="bi bi-calculator me-2"></i>Calculate Size
                </button>
              </div>
            </div>
          </div>

          <!-- Right: Result Panel -->
          <div class="kb-calc-result" data-aos="fade-left" data-aos-delay="100">
            <!-- Placeholder -->
            <div v-if="!customResult" class="kb-calc-result__empty">
              <i class="bi bi-rulers"></i>
              <h3>Enter your dimensions to calculate the tarpaulin size</h3>
              <p>Works for any surface — floors, roofs, vehicles, equipment, or construction sites.</p>
              <div class="kb-calc-result__formula-box">
                <p class="formula-title">Formula Used:</p>
                <code>Final Length = L + Extra Margin → round up to 3</code>
                <code>Final Width = W + Extra Margin → round up to 3</code>
              </div>
            </div>

            <!-- Result -->
            <div v-else class="kb-calc-result__card">
              <div class="kb-calc-result__vehicle-info">
                <span class="kb-calc-result__badge kb-calc-result__badge--purple">Custom</span>
                <h3>Your Custom Tarpaulin Size</h3>
                <div class="kb-calc-result__dims">
                  <div class="dim-item">
                    <span>Your Length</span><strong>{{ customResult.inputLength }} ft</strong>
                  </div>
                  <div class="dim-item">
                    <span>Your Width</span><strong>{{ customResult.inputWidth }} ft</strong>
                  </div>
                  <div class="dim-item">
                    <span>Margin</span><strong>{{ customResult.margin }} ft</strong>
                  </div>
                </div>
              </div>

              <div class="kb-calc-result__output">
                <p class="kb-calc-result__output-label">Recommended Tarpaulin Size</p>
                <div class="kb-calc-result__size">
                  <div class="size-box">
                    <span>{{ customResult.finalLength }} ft</span>
                    <small>Length</small>
                  </div>
                  <div class="size-multiply">×</div>
                  <div class="size-box">
                    <span>{{ customResult.finalWidth }} ft</span>
                    <small>Width</small>
                  </div>
                </div>
                <p class="kb-calc-result__note">
                  <i class="bi bi-info-circle me-1"></i>
                  Size rounded up to the nearest standard sheet size (multiple of 3 ft)
                </p>
              </div>

              <div class="kb-calc-result__breakdown">
                <p class="breakdown-title">How we calculated this:</p>
                <div class="breakdown-row">
                  <span>Length = {{ customResult.inputLength }} + {{ customResult.margin }} margin</span>
                  <strong>= {{ customResult.rawLength }} ft → {{ customResult.finalLength }} ft</strong>
                </div>
                <div class="breakdown-row">
                  <span>Width = {{ customResult.inputWidth }} + {{ customResult.margin }} margin</span>
                  <strong>= {{ customResult.rawWidth }} ft → {{ customResult.finalWidth }} ft</strong>
                </div>
              </div>

              <div class="kb-calc-result__cta">
                <a
                  :href="`https://wa.me/919892310123?text=Hello%2C%20I%20need%20a%20custom%20tarpaulin%20of%20size%20${customResult.finalLength}%20x%20${customResult.finalWidth}%20ft`"
                  target="_blank"
                  class="kb-btn kb-btn--whatsapp"
                >
                  <i class="bi bi-whatsapp me-2"></i>Get Quote for This Size
                </a>
                <button class="kb-btn kb-btn--outline" @click="resetCustom">
                  <i class="bi bi-arrow-counterclockwise me-2"></i>Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- SEO Content Block -->
    <section class="kb-calc-seo-section">
      <div class="container-xl">
        <div class="kb-calc-seo-grid" data-aos="fade-up">
          <div class="kb-calc-seo-card">
            <i class="bi bi-truck-front-fill"></i>
            <h3>Truck & Tempo Tarpaulin Sizes</h3>
            <p>From Tata Ace to 40 ft trailers — we manufacture truck tarpaulins in exact sizes. Use our vehicle calculator to find the right fit for any commercial vehicle in India.</p>
          </div>
          <div class="kb-calc-seo-card">
            <i class="bi bi-buildings-fill"></i>
            <h3>Warehouse & Godown Covers</h3>
            <p>Protect your stock during monsoon or open storage with large-format tarpaulins. Our warehouse calculator handles sizes from small godowns to mega industrial facilities.</p>
          </div>
          <div class="kb-calc-seo-card">
            <i class="bi bi-rulers"></i>
            <h3>Custom Size Manufacturing</h3>
            <p>Need an odd size that's not on the list? KeBee manufactures fully custom tarpaulins in any dimension. Use the custom calculator and WhatsApp us for a tailored quote.</p>
          </div>
          <div class="kb-calc-seo-card">
            <i class="bi bi-patch-check-fill"></i>
            <h3>100% Virgin Polymer Quality</h3>
            <p>All tarpaulins are made from 100% Virgin HDPE or PVC — no recycled content. UV stabilised, waterproof, and available in 125 to 750+ GSM. Pan-India delivery from Mumbai.</p>
          </div>
        </div>

        <!-- FAQ Block for SEO -->
        <div class="kb-calc-faq" data-aos="fade-up">
          <h2>Frequently Asked Questions</h2>
          <div class="kb-faq-list">
            <div class="kb-faq-item" v-for="faq in faqs" :key="faq.q" @click="faq.open = !faq.open">
              <div class="kb-faq-item__q">
                <span>{{ faq.q }}</span>
                <i :class="faq.open ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
              </div>
              <div class="kb-faq-item__a" v-show="faq.open">{{ faq.a }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Banner -->
    <section class="kb-cta-banner">
      <div class="container-xl">
        <div class="kb-cta-banner__content" data-aos="fade-up">
          <h2>Got Your Size? Order from KeBee Industries</h2>
          <p>Mumbai-based manufacturer. Custom sizes. Bulk orders. Pan-India delivery.</p>
          <div class="kb-cta-banner__actions">
            <a
              href="https://wa.me/919892310123?text=Hello%2C%20I%20calculated%20my%20tarpaulin%20size%20and%20would%20like%20a%20quote"
              target="_blank"
              class="kb-btn kb-btn--primary"
            >
              <i class="bi bi-whatsapp me-2"></i>WhatsApp for Custom Quote
            </a>
            <router-link to="/products" class="kb-btn kb-btn--outline-white">
              <i class="bi bi-grid-3x3-gap me-2"></i>Browse Products
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { setSeo } from '@/utils/seo.js'
import {
  vehicleData,
  warehouseData,
  vehicleCategories,
  warehouseCategories,
  roundUpTo3,
} from '@/data/calculatorData.js'

export default {
  name: 'CalculatorPage',
  components: { AppHeader, AppFooter },
  mounted() {
    this.syncTabFromRoute(this.$route)
  },
  watch: {
    $route(to) {
      this.syncTabFromRoute(to)
    },
  },
  data() {
    return {
      vehicleData,
      warehouseData,
      vehicleCategories,
      warehouseCategories,
      activeTab: this.$route.path.includes('warehouse') ? 'warehouse' : this.$route.path.includes('custom') ? 'custom' : 'vehicle',
      tabs: [
        { key: 'vehicle', label: 'Vehicle Calculator', icon: 'bi-truck-front' },
        { key: 'warehouse', label: 'Warehouse Calculator', icon: 'bi-buildings' },
        { key: 'custom', label: 'Custom Size Calculator', icon: 'bi-rulers' },
      ],
      // Vehicle
      selectedVehicleCategory: null,
      selectedVehicle: null,
      // Warehouse
      selectedWarehouseCategory: null,
      selectedWarehouse: null,
      // Custom
      custom: { length: null, width: null, margin: 1.5 },
      customResult: null,
      // FAQ
      faqs: [
        {
          q: 'How do I calculate the tarpaulin size for a truck?',
          a: 'Use the formula: Tarpaulin Length = Vehicle Length + (2 × Height) + Margin, and Tarpaulin Width = Vehicle Width + (2 × Height) + Margin. For example, a 14ft truck (14×6×6.5ft) with 1.5ft margin needs a tarpaulin of 14 + 13 + 1.5 = 28.5ft (round up to 30ft) × 6 + 13 + 1.5 = 20.5ft (round up to 21ft).',
          open: false,
        },
        {
          q: 'What size tarpaulin do I need for a Tata Ace?',
          a: 'A Tata Ace (7×4.8×4.8ft body) requires a tarpaulin of approximately 18ft × 15ft with standard overlap margin. We recommend a 18×15 ft or 21×18 ft tarpaulin for full coverage with secure tie-down area.',
          open: false,
        },
        {
          q: 'What is the standard tarpaulin size for a 20ft truck?',
          a: 'A 20ft truck with 7ft width and 7ft height requires a tarpaulin of 36ft × 24ft (rounded to standard sizes). Our calculator recommends 36×24ft based on the formula: 20 + 14 + 1.5 = 35.5 → 36ft length, 7 + 14 + 1.5 = 22.5 → 24ft width.',
          open: false,
        },
        {
          q: 'What does "round up to nearest 3 ft" mean?',
          a: 'Standard tarpaulin sheets are manufactured in multiples of 3 feet (e.g. 12×9, 15×12, 18×15, 21×18, etc.). If your calculation gives 28ft, we round up to 30ft — the next available standard size. This ensures you always have enough coverage without a custom cut surcharge.',
          open: false,
        },
        {
          q: 'Can KeBee manufacture custom size tarpaulins?',
          a: 'Yes. KeBee Industries manufactures fully custom tarpaulins in any length and width. Use the Custom Size Calculator above to find your dimensions, then WhatsApp us for a quote. Minimum order quantities apply for very unusual dimensions.',
          open: false,
        },
        {
          q: 'What type of tarpaulin is best for truck transport?',
          a: 'For truck transport, HDPE tarpaulins (150–250 GSM) are ideal for general cargo, while PVC-coated tarpaulins (350–600 GSM) are recommended for heavy-duty, high-value, or chemical cargo where waterproofing is critical. KeBee offers both in truck-specific sizes.',
          open: false,
        },
      ],
    }
  },
  computed: {
    filteredVehicles() {
      if (!this.selectedVehicleCategory) return []
      return this.vehicleData.filter(v => v.category === this.selectedVehicleCategory)
    },
    filteredWarehouses() {
      if (!this.selectedWarehouseCategory) return []
      return this.warehouseData.filter(w => w.category === this.selectedWarehouseCategory)
    },
  },
  methods: {
    roundUpTo3,
    selectVehicleCategory(cat) {
      this.selectedVehicleCategory = cat
      this.selectedVehicle = null
    },
    selectWarehouseCategory(cat) {
      this.selectedWarehouseCategory = cat
      this.selectedWarehouse = null
    },
    computeCustom() {
      const { length, width, margin } = this.custom
      if (!length || !width) { this.customResult = null; return }
      const m = margin || 0
      const rawLength = length + m
      const rawWidth = width + m
      this.customResult = {
        inputLength: length,
        inputWidth: width,
        margin: m,
        rawLength,
        rawWidth,
        finalLength: roundUpTo3(rawLength),
        finalWidth: roundUpTo3(rawWidth),
      }
    },
    resetCustom() {
      this.custom = { length: null, width: null, margin: 1.5 }
      this.customResult = null
    },
    resetAll() {
      this.selectedVehicleCategory = null
      this.selectedVehicle = null
      this.selectedWarehouseCategory = null
      this.selectedWarehouse = null
      this.resetCustom()
    },
    scrollToResult() {
      this.$nextTick(() => {
        const el = document.querySelector('.kb-calc-result__card')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    scrollToTabs() {
      this.$nextTick(() => {
        const el = document.querySelector('.kb-calc-tabs-bar')
        if (el) {
          const yOffset = -20
          const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset
          window.scrollTo({ top: y, behavior: 'smooth' })
        }
      })
    },
    syncTabFromRoute(route) {
      if (route.path.includes('warehouse')) {
        this.activeTab = 'warehouse'
        this.scrollToTabs()
        setSeo({
          title: 'Warehouse Tarpaulin Size Calculator – Godown & Storage Cover Sizes | KeBee Industries',
          description: 'Calculate the exact tarpaulin size for your warehouse, godown or storage shed. Covers small godowns to mega industrial facilities. Free tool by KeBee Industries, Mumbai.',
          path: '/calculator/warehouse',
          keywords: 'warehouse tarpaulin size, godown tarpaulin size, storage shed tarpaulin, industrial tarpaulin cover, waterproof warehouse cover, tarpaulin for godown India',
        })
      } else if (route.path.includes('custom')) {
        this.activeTab = 'custom'
        this.scrollToTabs()
        setSeo({
          title: 'Custom Tarpaulin Size Calculator – Any Dimension | KeBee Industries',
          description: 'Enter your own length, width and overlap margin to calculate the exact tarpaulin size you need. Works for any surface, vehicle or structure. Custom tarpaulin manufacturer in Mumbai.',
          path: '/calculator/custom',
          keywords: 'custom tarpaulin size calculator, tarpaulin size by dimension, how to calculate tarpaulin size, tarpaulin measurement calculator, custom tarpaulin India',
        })
      } else {
        this.activeTab = 'vehicle'
        this.scrollToTabs()
        setSeo({
          title: 'Truck & Vehicle Tarpaulin Size Calculator – Tata Ace to 40ft Trailer | KeBee Industries',
          description: 'Free tarpaulin size calculator for all trucks and tempos in India — Tata Ace, Mahindra Jeeto, Tata 407, 14ft, 20ft, 32ft and 40ft trailer. Get the exact size instantly. KeBee Industries, Mumbai.',
          path: '/calculator/vehicle',
          keywords: 'truck tarpaulin size, lorry tarpaulin size, tempo tarpaulin size, tarpaulin for Tata Ace, tarpaulin for Tata 407, tarpaulin size chart India, vehicle tarpaulin calculator',
        })
      }
    },
  },
}
</script>

<style scoped>
/* ── Hero ── */
.kb-calc-hero {
  background: url('@/assets/truck-tarpaulin.webp') center center / cover no-repeat;
  min-height: 380px;
  position: relative;
  display: flex;
  align-items: flex-end;
  padding-bottom: 60px;
}
.kb-calc-hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(15,31,61,0.72) 0%, rgba(15,31,61,0.92) 100%);
}
.kb-calc-hero__content {
  position: relative;
  z-index: 2;
  padding-top: 120px;
}
.kb-page-hero__breadcrumb {
  display: flex;
  align-items: center;
  font-size: 0.83rem;
  color: rgba(255,255,255,0.6);
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.kb-page-hero__breadcrumb a { color: rgba(255,255,255,0.6); text-decoration: none; }
.kb-page-hero__breadcrumb a:hover { color: #F59E0B; }
.kb-calc-hero__content h1 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.75rem;
}
.kb-calc-hero__content p {
  color: rgba(255,255,255,0.8);
  font-size: 1.05rem;
  margin-bottom: 1.5rem;
}
.kb-calc-hero__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}
.kb-calc-hero__badges span {
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 50px;
  backdrop-filter: blur(4px);
}

/* ── Tabs Bar ── */
.kb-calc-tabs-bar {
  background: #fff;
  border-bottom: 2px solid #E5E7EB;
  position: sticky;
  top: 72px;
  z-index: 100;
  box-shadow: 0 2px 12px rgba(15,31,61,0.06);
}
.kb-calc-tabs {
  display: flex;
  gap: 0;
  overflow-x: auto;
}
.kb-calc-tab {
  flex: 1;
  min-width: 180px;
  padding: 18px 24px;
  border: none;
  background: transparent;
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 3px solid transparent;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kb-calc-tab:hover { color: #0F1F3D; background: #F9FAFB; }
.kb-calc-tab.active {
  color: #0F1F3D;
  border-bottom-color: #F59E0B;
  background: rgba(245,158,11,0.04);
}

/* ── Section ── */
.kb-calc-section {
  padding: 60px 0;
  background: #F0F4F8;
}

/* ── Layout ── */
.kb-calc-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
  align-items: start;
}

/* ── Input Panel ── */
.kb-calc-panel {
  background: #fff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 24px rgba(15,31,61,0.07);
}
.kb-calc-panel__header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #F3F4F6;
}
.kb-calc-panel__header > i {
  font-size: 2rem;
  color: #F59E0B;
  flex-shrink: 0;
  margin-top: 2px;
}
.kb-calc-panel__header h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
  color: #0F1F3D;
  margin: 0 0 4px;
}
.kb-calc-panel__header p {
  font-size: 0.85rem;
  color: #6B7280;
  margin: 0;
}

.kb-calc-step {
  margin-bottom: 1.75rem;
}
.kb-calc-label {
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #0F1F3D;
  margin-bottom: 0.875rem;
}

/* Category Grid */
.kb-calc-category-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}
.kb-calc-category-grid--2 {
  grid-template-columns: repeat(2, 1fr);
}
.kb-calc-cat-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 14px 8px;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  background: #F9FAFB;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}
.kb-calc-cat-btn i {
  font-size: 1.4rem;
  color: #9CA3AF;
}
.kb-calc-cat-btn span {
  font-family: 'Poppins', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  color: #374151;
}
.kb-calc-cat-btn small {
  font-size: 0.65rem;
  color: #9CA3AF;
  line-height: 1.2;
}
.kb-calc-cat-btn:hover { border-color: #F59E0B; background: #FFFBEB; }
.kb-calc-cat-btn.active {
  border-color: #F59E0B;
  background: #FFFBEB;
}
.kb-calc-cat-btn.active i { color: #F59E0B; }
.kb-calc-cat-btn.active span { color: #0F1F3D; }

/* Vehicle List */
.kb-calc-vehicle-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.kb-calc-vehicle-btn {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 12px 16px;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  background: #F9FAFB;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
}
.kb-calc-vehicle-btn i:first-child { font-size: 1.1rem; color: #9CA3AF; flex-shrink: 0; }
.kb-calc-vehicle-btn div { flex: 1; }
.kb-calc-vehicle-btn div strong { display: block; font-size: 0.88rem; color: #0F1F3D; font-weight: 600; }
.kb-calc-vehicle-btn div small { font-size: 0.75rem; color: #9CA3AF; }
.kb-calc-vehicle-btn .arrow { font-size: 0.8rem; color: #D1D5DB; }
.kb-calc-vehicle-btn:hover { border-color: #F59E0B; background: #FFFBEB; }
.kb-calc-vehicle-btn.active {
  border-color: #0F1F3D;
  background: #0F1F3D;
}
.kb-calc-vehicle-btn.active strong { color: #fff; }
.kb-calc-vehicle-btn.active small { color: rgba(255,255,255,0.6); }
.kb-calc-vehicle-btn.active i { color: #F59E0B; }

/* ── Result Panel ── */
.kb-calc-result {
  background: #fff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 24px rgba(15,31,61,0.07);
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

/* Empty state */
.kb-calc-result__empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  color: #9CA3AF;
}
.kb-calc-result__empty > i {
  font-size: 3.5rem;
  color: #E5E7EB;
  margin-bottom: 1.25rem;
}
.kb-calc-result__empty h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: #6B7280;
  margin-bottom: 0.5rem;
}
.kb-calc-result__empty p {
  font-size: 0.85rem;
  color: #9CA3AF;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}
.kb-calc-result__formula-box {
  background: #F9FAFB;
  border-radius: 12px;
  padding: 16px 20px;
  text-align: left;
  width: 100%;
}
.formula-title { font-size: 0.75rem; font-weight: 700; color: #6B7280; margin: 0 0 8px; text-transform: uppercase; letter-spacing: 0.05em; }
.kb-calc-result__formula-box code {
  display: block;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  color: #0F1F3D;
  background: #F0F4F8;
  padding: 6px 10px;
  border-radius: 6px;
  margin-top: 6px;
}

/* Result card */
.kb-calc-result__card {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.kb-calc-result__vehicle-info h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #0F1F3D;
  margin: 8px 0 0.875rem;
}
.kb-calc-result__badge {
  display: inline-block;
  background: #0F1F3D;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 50px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.kb-calc-result__badge--green { background: #10B981; }
.kb-calc-result__badge--purple { background: #8B5CF6; }

.kb-calc-result__dims {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.dim-item {
  background: #F9FAFB;
  border-radius: 10px;
  padding: 10px 8px;
  text-align: center;
}
.dim-item span {
  display: block;
  font-size: 0.68rem;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}
.dim-item strong {
  font-family: 'Poppins', sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  color: #0F1F3D;
}

.kb-calc-result__output {
  background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%);
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
}
.kb-calc-result__output-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255,255,255,0.6);
  margin-bottom: 1rem;
}
.kb-calc-result__size {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.875rem;
}
.size-box {
  background: rgba(255,255,255,0.1);
  border: 2px solid rgba(245,158,11,0.5);
  border-radius: 12px;
  padding: 12px 20px;
  min-width: 90px;
}
.size-box span {
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 1.6rem;
  font-weight: 800;
  color: #F59E0B;
  line-height: 1;
}
.size-box small {
  display: block;
  font-size: 0.7rem;
  color: rgba(255,255,255,0.6);
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.size-multiply {
  font-family: 'Poppins', sans-serif;
  font-size: 1.8rem;
  font-weight: 300;
  color: rgba(255,255,255,0.4);
}
.kb-calc-result__note {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.5);
  margin: 0;
}

.kb-calc-result__breakdown {
  background: #F9FAFB;
  border-radius: 12px;
  padding: 1rem 1.25rem;
}
.breakdown-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9CA3AF;
  margin-bottom: 0.5rem;
}
.breakdown-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 0;
  border-bottom: 1px solid #F3F4F6;
  font-size: 0.8rem;
}
.breakdown-row:last-child { border-bottom: none; }
.breakdown-row span { color: #6B7280; flex: 1; }
.breakdown-row strong { color: #10B981; font-weight: 700; white-space: nowrap; }

.kb-calc-result__cta {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: auto;
}

/* ── Reference Table ── */
.kb-calc-table-section {
  background: #fff;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 24px rgba(15,31,61,0.07);
}
.kb-calc-table-title {
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
}
.kb-calc-table-wrap {
  overflow-x: auto;
}
.kb-calc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.kb-calc-table thead tr {
  background: #0F1F3D;
}
.kb-calc-table thead th {
  padding: 12px 16px;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255,255,255,0.8);
  white-space: nowrap;
}
.kb-calc-table tbody tr {
  border-bottom: 1px solid #F3F4F6;
  transition: background 0.15s;
}
.kb-calc-table tbody tr:last-child { border-bottom: none; }
.kb-calc-table tbody tr:hover { background: #FFF7ED; }
.kb-calc-table tbody tr.highlighted { background: #FFFBEB; }
.kb-calc-table td {
  padding: 12px 16px;
  color: #4B5563;
  vertical-align: middle;
  white-space: nowrap;
}
.kb-calc-table td strong { color: #0F1F3D; }
.kb-cat-pill {
  background: #0F1F3D;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 50px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.kb-cat-pill--green { background: #10B981; }
.kb-size-result {
  color: #10B981;
  font-size: 0.92rem;
}

/* ── Custom Form ── */
.kb-custom-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.kb-custom-field label {
  display: block;
  font-family: 'Poppins', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}
.kb-custom-field__hint {
  font-size: 0.75rem;
  color: #9CA3AF;
  margin: -2px 0 6px;
}
.kb-custom-input-wrap {
  display: flex;
  align-items: center;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  overflow: hidden;
  background: #F9FAFB;
  transition: border-color 0.2s;
}
.kb-custom-input-wrap:focus-within { border-color: #F59E0B; background: #fff; }
.kb-custom-input-wrap > i {
  padding: 0 12px;
  font-size: 1rem;
  color: #9CA3AF;
}
.kb-custom-input-wrap input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 12px 0;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  color: #0F1F3D;
  outline: none;
  width: 0;
}
.kb-custom-input-wrap span {
  padding: 0 14px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #9CA3AF;
  background: #F3F4F6;
  align-self: stretch;
  display: flex;
  align-items: center;
}
.kb-margin-presets {
  display: flex;
  gap: 0.5rem;
  margin-top: 8px;
}
.kb-margin-preset {
  padding: 5px 14px;
  border: 1.5px solid #E5E7EB;
  border-radius: 50px;
  background: #F9FAFB;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s;
}
.kb-margin-preset:hover { border-color: #F59E0B; color: #0F1F3D; }
.kb-margin-preset.active { background: #F59E0B; border-color: #F59E0B; color: #fff; }

/* ── SEO Section ── */
.kb-calc-seo-section {
  background: #fff;
  padding: 80px 0 60px;
}
.kb-calc-seo-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 4rem;
}
.kb-calc-seo-card {
  background: #F9FAFB;
  border-radius: 16px;
  padding: 1.75rem;
  border: 1px solid #F3F4F6;
  transition: box-shadow 0.2s;
}
.kb-calc-seo-card:hover { box-shadow: 0 8px 24px rgba(15,31,61,0.08); }
.kb-calc-seo-card > i {
  font-size: 1.75rem;
  color: #F59E0B;
  margin-bottom: 1rem;
  display: block;
}
.kb-calc-seo-card h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 0.5rem;
}
.kb-calc-seo-card p {
  font-size: 0.83rem;
  color: #6B7280;
  line-height: 1.7;
  margin: 0;
}

/* ── FAQ ── */
.kb-calc-faq h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 1.5rem;
}
.kb-faq-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  overflow: hidden;
}
.kb-faq-item {
  border-bottom: 1px solid #F3F4F6;
}
.kb-faq-item:last-child { border-bottom: none; }
.kb-faq-item__q {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  color: #0F1F3D;
  transition: background 0.15s;
  gap: 1rem;
}
.kb-faq-item__q:hover { background: #FFFBEB; }
.kb-faq-item__q i { flex-shrink: 0; color: #F59E0B; }
.kb-faq-item__a {
  padding: 0 24px 18px;
  font-size: 0.875rem;
  color: #6B7280;
  line-height: 1.8;
}

/* ── CTA Banner ── */
.kb-cta-banner {
  background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%);
  padding: 80px 0;
  text-align: center;
}
.kb-cta-banner__content h2 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
}
.kb-cta-banner__content p {
  color: rgba(255,255,255,0.75);
  font-size: 1.05rem;
  margin-bottom: 2rem;
}
.kb-cta-banner__actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

/* ── Responsive ── */
@media (max-width: 992px) {
  .kb-calc-layout { grid-template-columns: 1fr; }
  .kb-calc-seo-grid { grid-template-columns: repeat(2, 1fr); }
  .kb-calc-result__dims { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .kb-calc-tabs-bar { 
    top: 60px;
    position: relative;
  }
  .kb-calc-tabs { 
    flex-direction: column;
    gap: 0;
  }
  .kb-calc-tab { 
    font-size: 0.9rem; 
    padding: 16px 20px; 
    min-width: auto;
    width: 100%;
    border-bottom: 1px solid #E5E7EB;
    border-left: 3px solid transparent;
    justify-content: flex-start;
  }
  .kb-calc-tab.active {
    border-bottom-color: #E5E7EB;
    border-left-color: #F59E0B;
  }
  .kb-calc-category-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 576px) {
  .kb-calc-seo-grid { grid-template-columns: 1fr; }
  .kb-calc-result__size { gap: 0.5rem; }
  .size-box span { font-size: 1.2rem; }
  .kb-calc-tab { 
    font-size: 0.85rem; 
    padding: 14px 16px;
  }
}
</style>
